// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Stack } from '@fluentui/react';
import { useAboveSnowStyle, useBranding, useThemeName } from 'theme/SwitchableThemeProvider';
import { useIsMobile } from 'hooks/useIsMobile';
import { pruneUsers } from 'utils/pruneChatUsers';
import { isTeamsMeetingLinkLocator } from 'utils/joinMeeting';
import { TeamsMeetingLinkLocator } from '@azure/communication-calling';
import {
  CallAdapter,
  CallAndChatLocator,
  CallComposite,
  CallWithChatAdapter,
  CallWithChatComposite,
  CallWithChatCompositeOptions,
  CustomCallControlButtonCallback,
  CustomCallControlButtonProps,
  COMPOSITE_LOCALE_EN_US,
  useTheme,
  CallCompositeOptions,
  AttachmentUploadOptions,
  AttachmentUploadTask,
  TeamsCallAdapter
} from '@azure/communication-react';
import { attachmentUploadHandler } from 'clients/FileSharingClient';
import { SoundEffectsModal } from 'components/SoundAndVideoEffects/SoundEffects';
import { fetchProfileImageWithCaching } from 'utils/GraphApiServices';
import acsLogo from '../assets/acsLogo.svg';
import { useFunctionsAppToken, useGraphToken } from 'hooks/useUser';

export const Call = (props: { adapter: CallAdapter | TeamsCallAdapter; onHomeClick?: () => void }): JSX.Element => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const compositeContainerStyle = useAboveSnowStyle();
  const graphToken = useGraphToken();

  // Store tokens in refs as we don't want to trigger rerenders such as recreation of the fetchAvatarPersonaData function
  // when the token changes
  const graphTokenRef = useRef<string>();
  graphTokenRef.current = graphToken;

  const options = useMemo(
    (): CallCompositeOptions => ({
      surveyOptions: {
        onSurveyClosed: (): void => {
          props.onHomeClick?.();
        },
        onSurveySubmitted: (callID, surveyID, submittedSurvey, improvementSuggestions): Promise<void> => {
          props.onHomeClick?.();
          console.log(
            'call id is',
            callID,
            'survey id is ',
            surveyID,
            'call rating is ',
            submittedSurvey,
            'improvementsuggestion is ',
            improvementSuggestions
          );
          return Promise.resolve();
        }
      }
    }),
    [props.onHomeClick]
  );

  const fetchAvatarPersonaData = useCallback(async (userId: string) => {
    const isAADUser = userId.startsWith('8:orgid:');
    if (!graphTokenRef.current || !isAADUser) {
      return {};
    }

    const id = userId.replace('8:orgid:', '');
    const imageUrl = await fetchProfileImageWithCaching(id, { token: graphTokenRef.current });

    return { imageUrl };
  }, []);

  return (
    <Stack verticalAlign="center" verticalFill style={compositeContainerStyle}>
      <CallComposite
        adapter={props.adapter}
        fluentTheme={theme}
        formFactor={isMobile ? 'mobile' : 'desktop'}
        options={options}
        onFetchAvatarPersonaData={fetchAvatarPersonaData}
      />
    </Stack>
  );
};

export const CallWithChat = (props: {
  adapter: CallWithChatAdapter;
  locator?: CallAndChatLocator | TeamsMeetingLinkLocator;
  callTitle?: string;
  onHomeClick?: () => void;
}): JSX.Element => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const hasPruned = useRef(false);
  const { adapter, locator } = props;
  const graphToken = useGraphToken();
  const functionsAppToken = useFunctionsAppToken();

  // Store tokens in refs as we don't want to trigger rerenders such as recreation of the fetchAvatarPersonaData function
  // when the token changes
  const graphTokenRef = useRef<string>();
  graphTokenRef.current = graphToken;
  const functionsAppTokenRef = useRef<string>();
  functionsAppTokenRef.current = functionsAppToken;

  const isTeamsMeeting = locator && isTeamsMeetingLinkLocator(locator);

  useEffect(() => {
    const pruneUsersCallback = async (): Promise<void> => {
      if (!locator) {
        return;
      }

      if (!hasPruned.current && !isTeamsMeeting && functionsAppTokenRef.current) {
        pruneUsers({
          adapter: adapter,
          functionsAppToken: functionsAppTokenRef.current,
          threadId: (locator as CallAndChatLocator).chatThreadId
        }).then((pruneSuccessful) => {
          hasPruned.current = pruneSuccessful;
        });
      }
    };
    props.adapter.on('callParticipantsJoined', pruneUsersCallback);
    return () => props.adapter.off('callParticipantsJoined', pruneUsersCallback);
  }, [adapter, locator, isTeamsMeeting]);

  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const toggleCallout = useCallback(() => {
    setIsCalloutVisible(!isCalloutVisible);
  }, [isCalloutVisible, setIsCalloutVisible]);
  const [showSoundEffectsModal, setShowSoundEffectsModal] = useState(false);

  const [androidBackgroundBlurEnabled, setAndroidBackgroundBlurEnabled] = useState(false);
  const isAndroid = navigator.userAgent.toLowerCase().includes('android');

  const onFetchCustomButtonProps: CustomCallControlButtonCallback[] = useMemo(() => {
    const customButtons: CustomCallControlButtonCallback[] = [];
    if (isAndroid) {
      const backgroundBlurButton = (): CustomCallControlButtonProps => {
        return {
          iconName: 'BlurVideoBackground',
          strings: {
            label: androidBackgroundBlurEnabled ? 'Turn off background blur' : 'Turn on background blur'
          },
          placement: 'overflow',
          onItemClick: async () => {
            if (!androidBackgroundBlurEnabled) {
              await adapter.startVideoBackgroundEffect({
                effectName: 'blur'
              });
            } else {
              await adapter.stopVideoBackgroundEffects();
            }
            setAndroidBackgroundBlurEnabled((prev) => !prev);
          }
        };
      };
      customButtons.push(backgroundBlurButton);
    }
    const soundEffectsButton = (): CustomCallControlButtonProps => {
      return {
        showLabel: false,
        iconName: 'SoundEffects',
        strings: {
          label: 'Sound Effects',
          tooltipContent: 'Sound Effects'
        },
        placement: 'primary',
        onItemClick: () => {
          setShowSoundEffectsModal(true);
        }
      };
    };
    customButtons.push(soundEffectsButton);
    return customButtons;
  }, [isMobile, toggleCallout, androidBackgroundBlurEnabled, isAndroid, adapter]);

  const getAttachmentUploadOptions = useCallback((): AttachmentUploadOptions | undefined => {
    // if it is a teams meeting chat, disable file sharing
    // since file sharing in interop chat is not supported currently
    if (isTeamsMeeting) {
      return undefined;
    }
    return {
      handleAttachmentSelection: (attachmentUploads: AttachmentUploadTask[]) =>
        attachmentUploadHandler(attachmentUploads, functionsAppTokenRef.current)
    };
  }, []);

  const isDarkMode = useThemeName() === 'Dark';
  const branding = useBranding();
  const teamsMeetingBackground = isDarkMode ? '/teams-background-dark.png' : '/teams-background-light.png';
  const backgroundImageUrl =
    branding.customBranding?.backgroundImage?.url ?? (isTeamsMeeting ? teamsMeetingBackground : undefined);

  const options = useMemo(
    (): CallWithChatCompositeOptions => ({
      attachmentOptions: {
        // use default downloadHandler which is to open the file in a new tab
        uploadOptions: getAttachmentUploadOptions()
      },
      callControls: {
        onFetchCustomButtonProps: onFetchCustomButtonProps
      },
      branding: {
        logo: {
          url: acsLogo.toString(),
          alt: 'ACS Logo'
        },
        backgroundImage: backgroundImageUrl
          ? {
              url: backgroundImageUrl
            }
          : undefined
      },
      surveyOptions: {
        onSurveyClosed: (): void => {
          props.onHomeClick?.();
        },
        onSurveySubmitted: (callID, surveyID, submittedSurvey, improvementSuggestions): Promise<void> => {
          props.onHomeClick?.();
          console.log(
            'call id is',
            callID,
            'survey id is ',
            surveyID,
            'call rating is ',
            submittedSurvey,
            'improvementsuggestion is ',
            improvementSuggestions
          );
          return Promise.resolve();
        }
      }
    }),
    [onFetchCustomButtonProps, backgroundImageUrl, props.onHomeClick]
  );

  const locale = useMemo(() => {
    const locale = { ...COMPOSITE_LOCALE_EN_US };
    const callTitle = props.callTitle ?? (isTeamsMeeting ? 'Joining a Teams Meeting' : 'Joining an ACS Group Call');
    locale.strings.call.configurationPageTitle = callTitle;
    locale.strings.call.configurationPageCallDetails = 'Choose your audio and video settings';
    locale.strings.call.startCallButtonLabel = 'Join';
    return locale;
  }, [props.callTitle, isTeamsMeeting]);
  const compositeContainerStyle = useAboveSnowStyle();

  const fetchAvatarPersonaData = useCallback(async (userId: string) => {
    const isAADUser = userId.startsWith('8:orgid:');
    if (!graphTokenRef.current || !isAADUser) {
      return {};
    }

    const id = userId.replace('8:orgid:', '');
    const imageUrl = await fetchProfileImageWithCaching(id, { token: graphTokenRef.current });

    return { imageUrl };
  }, []);

  return (
    <>
      <Stack verticalAlign="center" verticalFill style={compositeContainerStyle}>
        <CallWithChatComposite
          adapter={props.adapter}
          locale={locale}
          fluentTheme={theme}
          formFactor={isMobile ? 'mobile' : 'desktop'}
          joinInvitationURL={window.location.href}
          options={options}
          onFetchAvatarPersonaData={fetchAvatarPersonaData}
        />
      </Stack>
      <SoundEffectsModal
        isOpen={showSoundEffectsModal}
        onDismiss={() => setShowSoundEffectsModal(false)}
        adapter={props.adapter}
      />
    </>
  );
};
